import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  obtenerVeterinarioPorToken,
  aceptarInvitacionVet,
  getCitasDeVet,
  rechazarCita,
  confirmarCita,
  marcarNoAsistio
} from "../services/api";
import AddAppointmentModal from "./components/AddAppointmentModal";
import GraciaPopup from "./components/GraciaPopup";
import AccesoBloqueado from "./components/AccesoBloqueado";
import "bootstrap/dist/css/bootstrap.min.css";
import toast from 'react-hot-toast';

// Brand colors - LemmonTags theme
const COLORS = {
  primary: "#84cc16",
  primaryDark: "#65a30d",
  primaryLight: "#ecfccb",
  premiumBg: "#f0fdf4",
  background: "#fafafa",
  cardBg: "#ffffff",
  textDark: "#1f2937",
  textMuted: "#6b7280",
  border: "#e5e7eb",
};

export default function VetPanel() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [vet, setVet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const [agendaTab, setAgendaTab] = useState("semana");
  const [appointments, setAppointments] = useState([]);
  const [showAddCita, setShowAddCita] = useState(false);
  const [showPremiumBanner, setShowPremiumBanner] = useState(true);

  // Estado para navegación del calendario
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Estado de acceso del veterinario
  const [acceso, setAcceso] = useState(null);
  const [showGraciaPopup, setShowGraciaPopup] = useState(false);

  useEffect(() => {
    async function loadVet() {
      if (!token) {
        setLoading(false);
        setError("No se recibio token en la URL.");
        return;
      }
      const resp = await obtenerVeterinarioPorToken(token);
      if (resp.error) {
        setError(resp.error);
      } else {
        setVet(resp.data);
      }
      setLoading(false);
    }
    loadVet();
  }, [token]);

  // Manejar estado de acceso cuando se carga el vet
  useEffect(() => {
    if (vet?.acceso) {
      setAcceso(vet.acceso);

      // Mostrar popup de gracia una vez por sesión
      if (vet.acceso.estado === 'gracia') {
        const yaVioPopup = sessionStorage.getItem('popup_gracia_visto');
        if (!yaVioPopup) {
          setShowGraciaPopup(true);
        }
      }
    }
  }, [vet]);

  // Función para cerrar el popup de gracia
  const cerrarPopupGracia = () => {
    sessionStorage.setItem('popup_gracia_visto', 'true');
    setShowGraciaPopup(false);
  };

  useEffect(() => {
    async function loadCitas() {
      if (!vet) return;
      try {
        const data = await getCitasDeVet(vet.id);
        setAppointments(data);
      } catch (err) {
        console.error("Error al cargar citas =>", err);
      }
    }
    loadCitas();
  }, [vet]);

  async function handleAcceptVet() {
    if (!vet) return;
    const resp = await aceptarInvitacionVet(vet.id);
    if (resp.error) {
      toast.error("Error: " + resp.error);
    } else {
      toast.success("Invitacion aceptada");
      setVet(resp.data);
    }
  }

  async function handleConfirmCita(citaId) {
    if (!vet) return;
    try {
      const citaActualizada = await confirmarCita(vet.id, citaId);
      setAppointments((prev) =>
        prev.map((cita) =>
          cita.id === citaId ? {
            ...cita,
            estado: "confirmada",
            snapshotCartillaUrl: citaActualizada?.snapshotCartillaUrl || null,
            snapshotExpedienteUrl: citaActualizada?.snapshotExpedienteUrl || null,
          } : cita
        )
      );
      toast.success("La cita ha sido confirmada.");
    } catch (err) {
      console.error("Error al confirmar cita =>", err);
      toast.error("No se pudo confirmar la cita.");
    }
  }

  async function handleCancelarCita(citaId) {
    if (!vet) return;
    try {
      await rechazarCita(vet.id, citaId);
      setAppointments((prev) =>
        prev.map((cita) =>
          cita.id === citaId ? { ...cita, estado: "rechazada" } : cita
        )
      );
      toast.success("La cita ha sido cancelada.");
    } catch (err) {
      console.error("Error al cancelar cita =>", err);
      toast.error(err.message || "No se pudo cancelar la cita.");
    }
  }

  async function handleMarcarNoAsistio(citaId) {
    if (!vet) return;
    try {
      await marcarNoAsistio(vet.id, citaId);
      setAppointments((prev) =>
        prev.map((cita) =>
          cita.id === citaId ? { ...cita, estado: "no_asistio" } : cita
        )
      );
      toast.success("La cita ha sido marcada como no asistio.");
    } catch (err) {
      console.error("Error al marcar no asistio =>", err);
      toast.error(err.message || "No se pudo marcar la cita como no asistio.");
    }
  }

  function handleCitaCreated() {
    setShowAddCita(false);
    if (vet) {
      getCitasDeVet(vet.id).then((data) => setAppointments(data));
    }
    toast.success("Cita creada exitosamente");
  }

  function handleLogout() {
    navigate("/vetLogin");
  }

  // Funciones de navegación del calendario
  function goToPrevMonth() {
    setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }

  function goToNextMonth() {
    setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }

  function goToToday() {
    setSelectedDate(new Date());
  }

  // Formatear nombre del mes
  function getMonthYearLabel(date) {
    const months = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  if (loading) {
    return (
      <div style={{ backgroundColor: COLORS.background, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="text-center">
          <div className="spinner-border" style={{ color: COLORS.primary }} role="status" />
          <p className="mt-3" style={{ color: COLORS.textMuted }}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isExpired = error.includes("expirado");
    const isInvalid = error.includes("invalido") || error.includes("inválido");

    return (
      <div style={{ backgroundColor: COLORS.background, minHeight: "100vh" }}>
        {/* Header */}
        <BrandHeader />

        <div className="container py-5">
          <div className="card shadow-sm border-0" style={{ maxWidth: "500px", margin: "0 auto" }}>
            <div className="card-body text-center p-4">
              <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                {isExpired ? "⏰" : isInvalid ? "🔗" : "⚠️"}
              </div>
              <h4 style={{ color: COLORS.textDark }}>
                {isExpired ? "Enlace Vencido" : isInvalid ? "Enlace Invalido" : "Error"}
              </h4>
              <p style={{ color: COLORS.textMuted }}>
                {isExpired ? "Tu enlace de acceso ha caducado." : isInvalid ? "El link que usaste no es valido." : error}
              </p>
              <button className="btn" style={{ backgroundColor: COLORS.primary, color: "white" }} onClick={() => navigate("/vetLogin")}>
                Solicitar nuevo acceso
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!vet) {
    return (
      <div style={{ backgroundColor: COLORS.background, minHeight: "100vh" }}>
        <BrandHeader />
        <div className="container py-5 text-center">
          <p style={{ color: COLORS.textMuted }}>No se encontro informacion del veterinario.</p>
        </div>
      </div>
    );
  }

  // Verificar si el acceso está bloqueado
  if (acceso?.estado === 'bloqueado' || acceso?.tieneAcceso === false) {
    return <AccesoBloqueado />;
  }

  // Filter appointments - usa selectedDate para navegación por mes
  let filteredAppointments = appointments;

  if (agendaTab === "semana") {
    // La semana se calcula basada en selectedDate
    const baseDate = selectedDate;
    const dayOfWeek = baseDate.getDay();
    const offset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startOfWeek = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + offset);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 7);
    filteredAppointments = appointments.filter((cita) => {
      if (!cita.fecha) return false;
      const citaDate = new Date(cita.fecha);
      return citaDate >= startOfWeek && citaDate < endOfWeek;
    });
  } else if (agendaTab === "mes") {
    // El mes se calcula basado en selectedDate
    const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
    filteredAppointments = appointments.filter((cita) => {
      if (!cita.fecha) return false;
      const citaDate = new Date(cita.fecha);
      return citaDate >= startOfMonth && citaDate < endOfMonth;
    });
  }

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: "100vh" }}>
      {/* Popup de período de gracia */}
      {showGraciaPopup && acceso && (
        <GraciaPopup
          diasRestantes={acceso.diasRestantes}
          pacientesEnGracia={acceso.pacientesEnGracia || []}
          onClose={cerrarPopupGracia}
        />
      )}

      {/* Header with Branding */}
      <BrandHeader vetName={vet.nombre} onLogout={handleLogout} />

      {/* Premium Banner */}
      {showPremiumBanner && (
        <div style={{ backgroundColor: COLORS.premiumBg, borderBottom: `1px solid ${COLORS.primaryLight}` }}>
          <div className="container py-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-3">
                <div style={{
                  backgroundColor: COLORS.primaryLight,
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={COLORS.primaryDark} strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </div>
                <div>
                  <p style={{ margin: 0, color: COLORS.textDark, fontWeight: 500, fontSize: "14px" }}>
                    Desbloquea Premium: Expediente clinico, notificaciones automaticas y agenda completa
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <a
                  href="https://lemmontags.com/veterinarios-premium"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm"
                  style={{ backgroundColor: COLORS.primary, color: "white", border: "none" }}
                >
                  Mas informacion
                </a>
                <button
                  className="btn btn-sm"
                  style={{ background: "transparent", border: "none", padding: "4px" }}
                  onClick={() => setShowPremiumBanner(false)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={COLORS.textMuted} strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container py-4">
        {/* Welcome Section */}
        <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
          <div>
            <h3 style={{ color: COLORS.textDark, fontWeight: 600, marginBottom: "4px", fontSize: "21px" }}>
              Hola, Dr(a). {vet.nombre}
            </h3>
            <p style={{ color: COLORS.textMuted, margin: 0 }}>Bienvenido a tu panel de veterinario</p>
            {vet.estado === "pendiente" && (
              <button className="btn btn-sm mt-2" style={{ backgroundColor: COLORS.primary, color: "white" }} onClick={handleAcceptVet}>
                Aceptar Invitacion
              </button>
            )}
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button
              className="btn"
              style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }}
              onClick={() => navigate(`/vetPanel/pacientes?vetId=${vet.id}&token=${token}`)}
            >
              Mis Pacientes
            </button>
            <button
              className="btn"
              style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }}
              onClick={() => navigate(`/vetPanel/calendarDisponibilidad?vetId=${vet.id}&token=${token}`)}
            >
              Mi Disponibilidad
            </button>
            <button
              className="btn"
              style={{
                backgroundColor: COLORS.cardBg,
                color: COLORS.textDark,
                border: `1px solid ${COLORS.border}`,
                position: 'relative'
              }}
              onClick={() => navigate(`/vetPanel/perfil?vetId=${vet.id}&token=${token}`)}
            >
              Mi Perfil
              {!vet.perfilProfesional?.perfilCompleto && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#ef4444',
                  borderRadius: '50%',
                  border: '2px solid white'
                }}></span>
              )}
            </button>
            <button
              className="btn"
              style={{ backgroundColor: COLORS.cardBg, color: COLORS.textDark, border: `1px solid ${COLORS.border}` }}
              onClick={() => navigate(`/vetPanel/seguimiento?vetId=${vet.id}&token=${token}`)}
            >
              Seguimiento
            </button>
          </div>
        </div>

        {/* Agenda Card */}
        <div className="card border-0 shadow-sm" style={{ backgroundColor: COLORS.cardBg }}>
          <div
            className="card-header d-flex justify-content-between align-items-center"
            style={{ backgroundColor: COLORS.primary, color: "white", borderBottom: "none" }}
          >
            <h5 className="mb-0" style={{ fontWeight: 600 }}>Mi Agenda</h5>
            <button
              className="btn btn-sm"
              style={{ backgroundColor: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)" }}
              onClick={() => setShowAddCita(true)}
            >
              + Nueva Cita
            </button>
          </div>

          <div className="card-body">
            {/* Navegación del calendario */}
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-sm"
                  style={{
                    backgroundColor: COLORS.cardBg,
                    color: COLORS.textDark,
                    border: `1px solid ${COLORS.border}`,
                    width: "36px",
                    height: "36px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  onClick={goToPrevMonth}
                  title="Mes anterior"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <span style={{
                  minWidth: "140px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: COLORS.textDark,
                  fontSize: "16px"
                }}>
                  {getMonthYearLabel(selectedDate)}
                </span>
                <button
                  className="btn btn-sm"
                  style={{
                    backgroundColor: COLORS.cardBg,
                    color: COLORS.textDark,
                    border: `1px solid ${COLORS.border}`,
                    width: "36px",
                    height: "36px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                  onClick={goToNextMonth}
                  title="Mes siguiente"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>

              {/* Botón Hoy y Tabs de vista */}
              <div className="d-flex gap-2 align-items-center">
                <button
                  className="btn btn-sm"
                  style={{
                    backgroundColor: COLORS.cardBg,
                    color: COLORS.primary,
                    border: `1px solid ${COLORS.primary}`,
                    fontWeight: 500,
                  }}
                  onClick={() => {
                    goToToday();
                    setAgendaTab("semana");
                  }}
                >
                  Hoy
                </button>
                {["semana", "mes"].map((tab) => (
                  <button
                    key={tab}
                    className="btn btn-sm"
                    style={{
                      backgroundColor: agendaTab === tab ? COLORS.primary : COLORS.cardBg,
                      color: agendaTab === tab ? "white" : COLORS.textDark,
                      border: `1px solid ${agendaTab === tab ? COLORS.primary : COLORS.border}`,
                      fontWeight: agendaTab === tab ? 600 : 400,
                    }}
                    onClick={() => setAgendaTab(tab)}
                  >
                    {tab === "semana" ? "Semana" : "Mes"}
                  </button>
                ))}
              </div>
            </div>

            {/* Appointments List */}
            {filteredAppointments.length === 0 ? (
              <div className="text-center py-5">
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>📅</div>
                <p style={{ color: COLORS.textMuted }}>No hay citas en esta vista</p>
                <button className="btn btn-sm" style={{ backgroundColor: COLORS.primary, color: "white" }} onClick={() => setShowAddCita(true)}>
                  Agendar primera cita
                </button>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {filteredAppointments.map((appt) => {
                  // Calcular si la cita ya paso
                  const fechaCita = new Date(appt.fecha + "T23:59:59");
                  const ahora = new Date();
                  const citaPasada = fechaCita < ahora;

                  // Determinar colores del badge segun estado
                  const getEstadoStyle = () => {
                    switch (appt.estado) {
                      case "confirmada": return { bg: "#dcfce7", color: "#166534" };
                      case "pendiente": return { bg: "#fef3c7", color: "#92400e" };
                      case "completada": return { bg: "#dbeafe", color: "#1e40af" };
                      case "no_asistio": return { bg: "#fef3c7", color: "#92400e" };
                      default: return { bg: "#fee2e2", color: "#991b1b" };
                    }
                  };
                  const estadoStyle = getEstadoStyle();

                  // Texto del estado
                  const getEstadoTexto = () => {
                    switch (appt.estado) {
                      case "confirmada": return "Confirmada";
                      case "pendiente": return "Pendiente";
                      case "completada": return "Completada";
                      case "no_asistio": return "No asistio";
                      case "rechazada": return "Cancelada";
                      default: return "Cancelada";
                    }
                  };

                  return (
                  <div
                    key={appt.id}
                    style={{ backgroundColor: COLORS.background, borderRadius: "8px", padding: "16px", border: `1px solid ${COLORS.border}` }}
                  >
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <span style={{ backgroundColor: COLORS.primaryLight, color: COLORS.primaryDark, padding: "4px 8px", borderRadius: "4px", fontSize: "13px", fontWeight: 600 }}>
                            {appt.fecha} - {appt.horaInicio}
                          </span>
                          <span style={{
                            backgroundColor: estadoStyle.bg,
                            color: estadoStyle.color,
                            padding: "4px 8px", borderRadius: "4px", fontSize: "12px", fontWeight: 500
                          }}>
                            {getEstadoTexto()}
                          </span>
                          {citaPasada && appt.estado === "confirmada" && (
                            <span style={{ backgroundColor: "#fef3c7", color: "#92400e", padding: "4px 8px", borderRadius: "4px", fontSize: "11px" }}>
                              Fecha pasada
                            </span>
                          )}
                        </div>
                        {appt.objetoName && (
                          <p style={{ margin: "4px 0", color: COLORS.textDark, fontWeight: 500 }}>{appt.objetoName}</p>
                        )}
                        {(appt.motivo || appt.tipoConsulta) && (
                          <p style={{ margin: "4px 0", color: COLORS.textMuted, fontSize: "14px" }}>Motivo: {appt.motivo || appt.tipoConsulta}</p>
                        )}
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {appt.estado === "pendiente" && (
                          <button className="btn btn-sm" style={{ backgroundColor: "#dcfce7", color: "#166534", border: "none" }} onClick={() => handleConfirmCita(appt.id)}>
                            Confirmar
                          </button>
                        )}
                        {/* Boton Cancelar: solo para citas futuras */}
                        {!citaPasada && appt.estado !== "rechazada" && appt.estado !== "completada" && appt.estado !== "no_asistio" && (
                          <button className="btn btn-sm" style={{ backgroundColor: "#fee2e2", color: "#991b1b", border: "none" }} onClick={() => handleCancelarCita(appt.id)}>
                            Cancelar
                          </button>
                        )}
                        {/* Boton No Asistio: solo para citas pasadas confirmadas */}
                        {citaPasada && appt.estado === "confirmada" && (
                          <button className="btn btn-sm" style={{ backgroundColor: "#fef3c7", color: "#92400e", border: "none" }} onClick={() => handleMarcarNoAsistio(appt.id)}>
                            No asistio
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Botones de acción para citas confirmadas */}
                    {appt.estado === "confirmada" && (
                      <div className="d-flex gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${COLORS.border}` }}>
                        <button
                          className="btn btn-sm"
                          style={{
                            backgroundColor: appt.snapshotCartillaUrl ? COLORS.cardBg : "#f3f4f6",
                            color: appt.snapshotCartillaUrl ? COLORS.textDark : "#9ca3af",
                            border: `1px solid ${COLORS.border}`,
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: appt.snapshotCartillaUrl ? "pointer" : "not-allowed"
                          }}
                          onClick={() => appt.snapshotCartillaUrl && window.open(appt.snapshotCartillaUrl, "_blank")}
                          disabled={!appt.snapshotCartillaUrl}
                          title={appt.snapshotCartillaUrl ? "Ver cartilla de vacunación" : "Generando..."}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          {appt.snapshotCartillaUrl ? "Cartilla" : "Generando..."}
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{
                            backgroundColor: appt.snapshotExpedienteUrl ? COLORS.cardBg : "#f3f4f6",
                            color: appt.snapshotExpedienteUrl ? COLORS.textDark : "#9ca3af",
                            border: `1px solid ${COLORS.border}`,
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            cursor: appt.snapshotExpedienteUrl ? "pointer" : "not-allowed"
                          }}
                          onClick={() => appt.snapshotExpedienteUrl && window.open(appt.snapshotExpedienteUrl, "_blank")}
                          disabled={!appt.snapshotExpedienteUrl}
                          title={appt.snapshotExpedienteUrl ? "Ver expediente clínico" : "Generando..."}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                          </svg>
                          {appt.snapshotExpedienteUrl ? "Expediente" : "Generando..."}
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{
                            backgroundColor: COLORS.primary,
                            color: "white",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px"
                          }}
                          onClick={() => navigate(`/vetPanel/atender/${appt.id}?vetId=${vet.id}&token=${token}`)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                          Atender
                        </button>
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center py-4" style={{ color: COLORS.textMuted }}>
        <small>© 2025 LemmonTags - Todos los derechos reservados</small>
      </footer>

      {/* Modal Nueva Cita */}
      {showAddCita && (
        <AddAppointmentModal
          vetId={vet.id}
          vetNombre={vet.nombre}
          onClose={() => setShowAddCita(false)}
          onSuccess={handleCitaCreated}
        />
      )}
    </div>
  );
}

/**
 * Brand Header Component
 * Logo on the left (clickable), vet name + logout on the right
 */
function BrandHeader({ vetName, onLogout }) {
  return (
    <header style={{
      backgroundColor: COLORS.cardBg,
      borderBottom: `1px solid ${COLORS.border}`,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
    }}>
      <div className="container py-3">
        <div className="d-flex justify-content-between align-items-center">
          {/* Logo */}
          <a
            href="https://lemmontags.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "8px" }}
          >
            <div style={{
              backgroundColor: COLORS.primary,
              borderRadius: "8px",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <span style={{
              fontSize: "1.25rem",
              fontWeight: 600,
              color: COLORS.textDark,
              letterSpacing: "-0.025em"
            }}>
              LemmonTags
            </span>
          </a>

          {/* Right side: Vet name + logout */}
          {vetName && onLogout && (
            <div className="d-flex align-items-center gap-3">
              <span style={{ color: COLORS.textMuted, fontSize: "14px" }}>
                Dr(a). {vetName}
              </span>
              <button
                className="btn btn-sm"
                style={{
                  backgroundColor: "transparent",
                  color: COLORS.textMuted,
                  border: `1px solid ${COLORS.border}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
                onClick={onLogout}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Cerrar sesion
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
